import React from 'react';
import confetti from 'canvas-confetti';
import styles from './Match.module.css';
import * as utils from '../../utils/utils';
import HQStreamerUtil from '../HQStreamerUtil/HQStreamerUtil';
import Sidebar from '../Sidebar/Sidebar';
import {
    FiArrowRight,
    FiBook,
    FiBriefcase,
    FiCheckCircle,
    FiChevronLeft,
    FiChevronRight,
    FiClock,
    FiHeart,
    FiLink,
    FiLink2,
    FiList,
    FiStar,
    FiThumbsUp,
    FiTrendingUp,
    FiUserPlus,
    FiX,
} from 'react-icons/fi';

const BATCH_SIZE = 5;

const MATCH_LABELS = {
    availabilityCertificateMatches: 'Availability + certificate',
    availabilityDomainMatches: 'Availability + domain',
    availabilityOfferMatches: 'Availability + offer',
    availabilitySkillMatches: 'Availability + skill',
    availabilitySoftSkillMatches: 'Availability + soft skill',
    availabilityToolMatches: 'Availability + tool',
    availabilityUserEntityLiveResumeMatches: 'Availability + live resume',
    certificateMatches: 'Certificates',
    courseMatches: 'Courses',
    domainMatches: 'Domains',
    followUserMatches: 'Follow graph',
    offerCertificateMatches: 'Offer + certificate',
    offerDomainMatches: 'Offer + domain',
    offerSkillMatches: 'Offer + skill',
    offerSoftSkillMatches: 'Offer + soft skill',
    offerToolMatches: 'Offer + tool',
    offerUserEntityLiveResumeMatches: 'Offer + live resume',
    skillMatches: 'Skills',
    softSkillMatches: 'Soft skills',
    toolMatches: 'Tools',
    userEntityLiveResumeAchievementMatches: 'Resume achievements',
    userEntityLiveResumeImpactMatches: 'Resume impacts',
    userEntityLiveResumeMatches: 'Live resumes',
    userEntityMatches: 'User entities',
    userFromInviteMatches: 'Invites',
    userSchoolMajorMatches: 'School + major',
};

const clampText = (value, limit) => {
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text) {
        return '';
    }

    if (text.length <= limit) {
        return text;
    }

    return `${text.slice(0, limit - 1).trimEnd()}…`;
};

const uniqueTextList = (value) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return Array.from(
        new Set(
            value
                .flat(Infinity)
                .map((item) => (typeof item === 'string' ? item.trim() : ''))
                .filter(Boolean)
        )
    );
};

export default class Match extends React.Component {
    constructor(props) {
        super(props);

        this.iconList = [
            <FiUserPlus />, // hasInvite
            <FiHeart />, // hasFollow
            <FiLink />, // availabilitiesOffers
            <FiStar />, // skills, tools, domains, softSkills, certificates
            <FiList />, // liveResumes
            <FiThumbsUp />, // impacts
            <FiTrendingUp />, // achievements
            <FiBook />, // schools
            <FiBriefcase />, // experiences
        ];

        this.state = {
            isLoading: true,
            errorText: '',
            cards: [],
            offset: 0,
            hasMore: true,
            activeIndex: 0,
            isAdvancing: false,
        };

        this.advanceTimer = null;
    };

    componentDidMount() {
        if (!utils.isLoggedIn()) {
            window.location.href = '/signin';
            return;
        }

        this.loadCards(true);
    }

    componentWillUnmount() {
        if (this.advanceTimer) {
            clearTimeout(this.advanceTimer);
            this.advanceTimer = null;
        }
    }

    loadCards = async (reset = false) => {
        const nextOffset = reset ? 0 : this.state.offset;

        this.setState({
            isLoading: true,
            errorText: '',
            ...(reset ? { cards: [], activeIndex: 0, offset: 0, hasMore: true, isAdvancing: false } : {}),
        });

        try {
                const response = await utils.sendRequest('/userConnection/getUserConnections', {
                    offset: nextOffset,
                });

            const users = Array.isArray(response.users) ? response.users : [];
            const hasMore = users.length === BATCH_SIZE;

            this.setState((prevState) => ({
                isLoading: false,
                cards: reset ? users : [...prevState.cards, ...users],
                offset: nextOffset + users.length,
                hasMore,
            }));
        } catch (error) {
            this.setState({
                isLoading: false,
                errorText: error && error.message ? error.message : 'Could not load your matches right now.',
            });
        }
    };

    getCurrentCard = () => {
        const { cards, activeIndex } = this.state;
        return cards[activeIndex] || null;
    };

    getUserSummary = (card) => {
        if (!card) {
            return null;
        }

        return card.otherUser || card.user || card.targetUser || null;
    };

    getUserDisplayName = (user) => {
        if (!user) {
            return 'Match';
        }

        const parts = [user.firstName, user.lastName].filter((part) => typeof part === 'string' && part.trim() !== '');
        if (parts.length > 0) {
            return parts.join(' ');
        }

        if (typeof user.nickname === 'string' && user.nickname.trim() !== '') {
            return `@${user.nickname.trim()}`;
        }

        return 'Match';
    };

    getUserAvatarText = (userFirstname) => {
        if (!userFirstname) {
            return 'HQ';
        }

        return userFirstname.trim().substr(0, 2).toUpperCase();
    };

    getUserAvatarUrl = (user) => {
        if (!user || typeof user.profileImageKey !== 'string' || user.profileImageKey.trim() === '') {
            return '';
        }

        return '';
    };

    getMatchMessageStream = (matches) => {
        if (!matches || typeof matches !== 'object') {
            return [];
        }

        const messageStream = [];

        if (matches.hasInvite) {
            messageStream.push({ icon: 0, message: 'Invited to HQ.' });
        }

        if (matches.hasFollow) {
            messageStream.push({ icon: 1, message: 'You both follow each other!' });
        }

        if (matches.hasLiveResume) {
            messageStream.push({ icon: 4, message: 'You both have common things in your live resumes!' });
        }

        if (matches.hasAchievement) {
            messageStream.push({ icon: 6, message: 'You both have common achievements!' });
        }

        if (matches.hasImpact) {
            messageStream.push({ icon: 5, message: 'You both have made similar impacts in the work you do!' });
        }

        if (matches.hasSchoolMajor) {
            messageStream.push({ icon: 7, message: 'You both have studied similar majors at similar schools!' });
        }

        if (matches.hasEntity) {
            messageStream.push({ icon: 8, message: 'You both have similar experiences in your profiles!' });
        }

        if (matches.hasAvailabilityOffer) {
            messageStream.push({ icon: 2, message: 'HQ also found you and them available for similar offers!' });
        }

        // const availabilitiesOffers = uniqueTextList(matches.availabilitiesOffers);
        // if (availabilitiesOffers.length > 0) {
        //     messageStream.push({
        //         icon: 2,
        //         message: `Something(s) common between what you both are available and offer: ${availabilitiesOffers.join(', ')}`,
        //     });
        // }

        const commonThings = uniqueTextList([
            matches.skills,
            matches.tools,
            matches.domains,
            matches.softSkills,
            matches.certificates,
        ]);
        if (commonThings.length > 0) {
            messageStream.push({
                icon: 3,
                message: `Here are some common things between you two: ${commonThings.join(', ')}`,
            });
        }

        // const liveResumes = uniqueTextList(matches.liveResumes);
        // if (liveResumes.length > 0) {
        //     messageStream.push({
        //         icon: 4,
        //         message: `Similar check-ins between you two: ${liveResumes.join(', ')}`,
        //     });
        // }

        // const commonImpactsAndAchievements = uniqueTextList([
        //     matches.impacts,
        //     matches.achievements,
        // ]);
        // if (commonImpactsAndAchievements.length > 0) {
        //     messageStream.push({
        //         icon: 5,
        //         message: `Common impacts and achievements: ${commonImpactsAndAchievements.join(', ')}`,
        //     });
        // }

        // const schools = uniqueTextList(matches.schools);
        // if (schools.length > 0) {
        //     messageStream.push({
        //         icon: 7,
        //         message: `Related education: ${schools.join(', ')}`,
        //     });
        // }

        // const experiences = uniqueTextList(matches.experiences);
        // if (experiences.length > 0) {
        //     messageStream.push({
        //         icon: 8,
        //         message: `Related work experiences: ${experiences.join(', ')}`,
        //     });
        // }

        return messageStream;
    };

    goToNextCard = () => {
        this.setState((prevState) => {
            const nextIndex = prevState.activeIndex + 1;
            const shouldLoadMore = nextIndex >= prevState.cards.length && prevState.hasMore && !prevState.isLoading;

            return {
                activeIndex: nextIndex,
                isAdvancing: false,
                ...(shouldLoadMore ? { isLoading: true } : {}),
            };
        }, async () => {
            const { activeIndex, cards, hasMore, isLoading } = this.state;

            if (activeIndex >= cards.length && hasMore && isLoading) {
                await this.loadCards(false);
            }
        });
    };

    onYesClick = async (card) => {
        if (this.state.isAdvancing) {
            return;
        }

        const userConnectionId = +card.userConnectionId;
        const userId = +card.userId;
        const targetUserId = +card.targetUserId;

        const response = await utils.sendRequest('/userConnection/approveConnection', {
            userConnectionId,
            userId,
            targetUserId,
        });

        if (!response || response.error) {
            alert('Failed to approve connection.');
            console.error('Approve Connection Error:', response?.error);
            return;
        }

        const isBothApproving = response?.isBothApproving;
        if (isBothApproving) {
            confetti({
                particleCount: 90,
                spread: 70,
                origin: { y: 0.68 },
            });
            alert('Both users have approved the connection!');
        }

        // if (!card || this.state.isAdvancing) {
        //     return;
        // }

        if (this.advanceTimer) {
            clearTimeout(this.advanceTimer);
        }

        this.setState({
            isAdvancing: true,
        });

        this.advanceTimer = window.setTimeout(() => {
            this.goToNextCard();
        }, 3000);
    };

    onNoClick = () => {
        if (this.state.isAdvancing) {
            return;
        }

        if (this.advanceTimer) {
            clearTimeout(this.advanceTimer);
            this.advanceTimer = null;
        }

        this.goToNextCard();
    };

    // renderMatchSection = (group) => {
    //     const items = Array.isArray(group.items) ? group.items : [];

    //     return (
    //         <section key={group.key} className={styles.matchGroup}>
    //             <div className={styles.matchGroupHeader}>
    //                 <span className={styles.matchGroupLabel}>{group.label}</span>
    //                 <span className={styles.matchGroupCount}>{items.length}</span>
    //             </div>

    //             <div className={styles.matchItemList}>
    //                 {items.slice(0, 3).map((item, index) => (
    //                     <div key={`${group.key}-${index}`} className={styles.matchItem}>
    //                         <div className={styles.matchItemTitle}>{this.formatMatchItem(group.key, item)}</div>
    //                         {Array.isArray(item.matchReasons) && item.matchReasons.length > 0 ? (
    //                             <div className={styles.matchReasonRow}>
    //                                 {item.matchReasons.slice(0, 3).map((reason) => (
    //                                     <span key={reason} className={styles.matchReasonChip}>{reason}</span>
    //                                 ))}
    //                             </div>
    //                         ) : null}
    //                     </div>
    //                 ))}
    //             </div>
    //         </section>
    //     );
    // };

    renderCard = (card, depth, isActive) => {
        // const user = card;
        // const displayName = this.getUserDisplayName(user);
        // const avatarText = this.getUserAvatarText(user);
        // const avatarUrl = this.getUserAvatarUrl(user);

        const {
            userFirstNameA,
            userNicknameA,
            userFirstNameB,
            userNicknameB,
        } = card;

        const avatarUrlA = null;
        const avatarUrlB = null;
        const avatarTextA = this.getUserAvatarText(userFirstNameA);
        const avatarTextB = this.getUserAvatarText(userFirstNameB);

        const messageStream = this.getMatchMessageStream(card.matches);
        const introMessage = 'If they also choose "Yes" for you, HQ will intro you to them!';

        return (
            <article
                key={`${card.userConnectionId || card.otherUserId}-${depth}`}
                className={`${styles.card} ${isActive ? styles.cardActive : styles.cardIdle}`}
                style={{
                    transform: `translateY(${depth * 14}px) scale(${1 - (depth * 0.03)})`,
                    opacity: isActive ? 1 : Math.max(0.28, 1 - (depth * 0.17)),
                    zIndex: 10 - depth,
                }}
            >
                <div className={styles.cardGlow} />
                <div className={styles.cardHeader}>
                    <div className={styles.userA}>
                        <div className={styles.cardIdentityA}>
                            {/* <div className={styles.cardEyebrow}>
                                <FiStar />
                                <span>Suggested intro</span>
                            </div> */}
                            <h2 className={styles.cardName}>{userFirstNameA}</h2>
                            <p className={styles.cardHandle}>
                                @{userNicknameA}
                            </p>
                        </div>

                        <div className={styles.avatarWrap}>
                            {avatarUrlA ? (
                                <img className={styles.avatarImage} src={avatarUrlA} />
                            ) : (
                                <div className={styles.avatarImageFallback}>
                                    {avatarTextA}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.userLinkIcons}>
                        <FiChevronLeft size={20} />
                        <FiLink2 size={20} />
                        <FiChevronRight size={20} />
                    </div>
                    <div className={styles.userB}>
                        <div className={styles.avatarWrap}>
                            {avatarUrlB ? (
                                <img className={styles.avatarImage} src={avatarUrlB} />
                            ) : (
                                <div className={styles.avatarImageFallback}>
                                    {avatarTextB}
                                </div>
                            )}
                        </div>

                        <div className={styles.cardIdentityB}>
                            {/* <div className={styles.cardEyebrow}>
                                <FiStar />
                                <span>Suggested intro</span>
                            </div> */}
                            <h2 className={styles.cardName}>{userFirstNameB}</h2>
                            <p className={styles.cardHandle}>
                                @{userNicknameB}
                            </p>
                        </div>
                    </div>

                </div>

                <div className={styles.cardBody}>
                    <HQStreamerUtil
                        key={`match-stream-${card.userConnectionId || card.otherUserId}`}
                        dataStream={messageStream}
                        iconList={this.iconList}
                    />
                    {/* {isActive && this.state.introMessageUserId === (user ? user.userId : card.otherUserId) ? (
                        <div className={styles.introMessagePanel}>
                            <FiCheckCircle className={styles.introMessageIcon} />
                            <p>{introMessage}</p>
                        </div>
                    ) : (
                        <>
                            <div className={styles.matchSummaryPill}>
                                <FiClock />
                                <span>{messageStream.length} matching signals</span>
                            </div>

                            {messageStream.length > 0 ? (
                                <HQStreamerUtil
                                    key={`match-stream-${card.userConnectionId || card.otherUserId}`}
                                    dataStream={messageStream}
                                    iconList={this.iconList}
                                />
                            ) : (
                                <div className={styles.emptyMatchState}>
                                    <FiArrowRight />
                                    <span>No detailed match signals were returned for this profile yet.</span>
                                </div>
                            )}
                        </>
                    )} */}
                </div>

                {/* {isActive && this.state.introMessageUserId !== (user ? user.userId : card.otherUserId) ? ( */}
                { isActive && (
                    <div className={styles.cardActions}>
                        <button
                            type="button"
                            className={styles.primaryAction}
                            onClick={() => this.onYesClick(card)}
                            disabled={this.state.isAdvancing}
                        >
                            Yes, I'd like HQ to intro
                        </button>

                        <button
                            type="button"
                            className={styles.secondaryAction}
                            onClick={this.onNoClick}
                            disabled={this.state.isAdvancing}
                        >
                            No thanks, skip for now
                        </button>
                    </div>
                )}
                {/* ) : null} */}
            </article>
        );
    };

    render() {
        const { isLoading, errorText, cards, activeIndex } = this.state;
        const currentCard = this.getCurrentCard();
        const visibleCards = cards.slice(activeIndex, activeIndex + 3);
        const hasCards = visibleCards.length > 0;
        const canLoadMore = !isLoading && this.state.hasMore;

        return (
            <div className={styles.pageContainer}>
                <Sidebar />

				<div className={styles.glowTop} />
				<div className={styles.glowBottom} />

                <main className={styles.mainContent}>
                    <section className={styles.heroBlock}>
                        <div className={styles.heroEyebrow}>Professional matching</div>
                        <h1 className={styles.heroTitle}>Your Matches</h1>
                        <p className={styles.heroSubtitle}>If they also match you, HQ will intro you both</p>
                    </section>

                    <section className={styles.deckShell}>
                        <div className={styles.deckFrame}>
                            {isLoading && cards.length === 0 ? (
                                <div className={styles.loadingState}>
                                    <div className={styles.loadingOrb} />
                                    <p>Finding your strongest professional matches…</p>
                                </div>
                            ) : errorText ? (
                                <div className={styles.errorState}>
                                    <p>{errorText}</p>
                                    <button type="button" className={styles.retryButton} onClick={() => this.loadCards(true)}>
                                        Try again
                                    </button>
                                </div>
                            ) : hasCards ? (
                                <div className={styles.cardStack}>
                                    {visibleCards.map((card, index) => this.renderCard(card, index, index === 0))}
                                </div>
                            ) : (
                                <div className={styles.emptyState}>
                                    <h2>You're all caught up.</h2>
                                    <p>Check back later for more intros, or refresh this screen to load the next batch.</p>
                                    {canLoadMore ? (
                                        <button type="button" className={styles.retryButton} onClick={() => this.loadCards(false)}>
                                            Load more matches
                                        </button>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </section>
                </main>
            </div>
        );
    }
}